import React, { useEffect, useState } from "react";
import { Badge, Button, Tooltip } from "antd";
import {
  ApartmentOutlined,
  CheckCircleFilled,
  FilterOutlined,
  TableOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import SplitPlanModal from "../modal/SplitPlanModal";
import { useLocation, useParams } from "react-router-dom";
import PageLoader from "../sub/PageLoader";
import GetSplitPlanApi from "../../api/split-plans/GetSplitPlanApi";
import { useSelector } from "react-redux";
import SplitsTree from "../tree/SplitsTree";
import SplitsTable from "../tables/SplitsTable";
import SplitsFilterModal from "../modal/filters/SplitsFilterModal";
import { permissionAbility } from "../../helpers/permissionHelpers";
import SplitPlanTreeCards from "./SplitPlanTreeCards";
import GetFilteredSplitPlanApi from "../../api/split-plans/GetFilteredSplitPlanAPI";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

function TopPlanPlans(props) {
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [data, setData] = useState([]);
  const [tree, setTree] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterBy, setFilterBy] = useState();
  const proHeight = document.body.clientHeight;
  let curLocation = useLocation();
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const [startAt, setStartAt] = useState(0);
  const [totalSplitPlanCount, setTotalSplitPlanCount] = useState(0);
  const { id } = useParams();
  const limit = LIMIT_PER_PAGE;

  useEffect(() => {
    getPlans(startAt);
  }, [curLocation]);

  async function getPlans(num, newSet) {
    setTableLoading(true);
    const apiResponse = await GetSplitPlanApi(id, num, limit);
    if (apiResponse.success) {
      if (apiResponse.data?.plans?.length !== 0) {
        if (newSet) {
          setData(apiResponse.data?.plans);
        } else {
          setData([...data, ...apiResponse.data?.plans]);
        }

        setCanShow(true);
        setTotalSplitPlanCount(apiResponse.data?.totalCount);
      }
    }
    setCanStart(true);
    setTableLoading(false);
  }

  async function getFilteredPlans(values, startAt, newSet) {
    setTableLoading(true);
    let path = curLocation.pathname;
    let id = path.split("/top-plans/");
    const apiResponse = await GetFilteredSplitPlanApi(
      id[1],
      values.planLevel,
      startAt,
      limit
    );
    if (apiResponse.success) {
      setFilterStatus(true);
      setTableLoading(false);
      setFilterBy({
        title: values.title,
        planLevel: values.planLevel,
      });

      if (newSet) {
        setData(apiResponse.data?.plans);
      } else {
        setData([...data, ...apiResponse.data?.plans]);
      }
      setTotalSplitPlanCount(apiResponse.data?.totalCount);
      if (apiResponse.data?.plans?.length !== 0) {
        setCanShow(true);
      }
    }
    setCanStart(true);
  }

  function showEmptyPlans() {
    return (
      <div
        style={{ height: proHeight - 188 }}
        className="flex justify-center items-center w-full h-full"
      >
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No plans available
          </div>
          {permissionAbility(userLevel, "plans", "split") && (
            <>
              <div className="mb-3 text-gray-400">Split plan to this plan</div>
              <Button onClick={() => setAddModal(true)} type="primary">
                Split Plan
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  function resetFilter() {
    setData([]);
    setFilterStatus(false);

    getPlans(0, true);
    setFilterBy();
  }

  function loadMore(num) {
    if (filterStatus) {
      getFilteredPlans(filterBy, num);
    } else {
      getPlans(num);
    }
  }
  function handleDelete(planId) {
    setTotalSplitPlanCount(totalSplitPlanCount - 1);
    const newData = [];
    data.forEach((element) => {
      if (planId !== element.planId) {
        newData.push(element);
      }
    });
    setData(newData);
    if (totalSplitPlanCount - 1 === 0) {
      setCanShow(false);
    }
  }
  function handleEdit(editedPlan) {
    const newData = [];
    data.forEach((element) => {
      if (editedPlan.planId === element.planId) {
        newData.push(editedPlan);
      } else {
        newData.push(element);
      }
    });
    setData(newData);
  }
  return (
    <>
      {canStart ? (
        canShow ? (
          <>
            <div className="flex justify-between items-center mb-2">
              {!tree ? (
                <Button onClick={() => setTree(true)}>
                  <ApartmentOutlined />
                </Button>
              ) : (
                <Button onClick={() => setTree(false)}>
                  <TableOutlined />
                </Button>
              )}
              <div className="flex">
                <>
                  <Tooltip title={"Reset"}>
                    <Button
                      disabled={!filterStatus}
                      onClick={() => resetFilter()}
                      className="mr-3"
                    >
                      <UndoOutlined />
                    </Button>
                  </Tooltip>
                  <Tooltip title={"Filter"}>
                    <Badge
                      count={
                        filterStatus ? (
                          <CheckCircleFilled style={{ color: "#f5222d" }} />
                        ) : (
                          0
                        )
                      }
                    >
                      <Button onClick={() => setFilterModal(true)}>
                        <FilterOutlined />
                      </Button>
                    </Badge>
                  </Tooltip>
                </>
                {permissionAbility(userLevel, "plans", "split") && (
                  <Button className="ml-3" onClick={() => setAddModal(true)}>
                    Split
                  </Button>
                )}
              </div>
            </div>
            {!tree ? (
              <div className="mb-5">
                <SplitPlanTreeCards
                  data={data}
                  loading={tableLoading}
                  loadMore={(num) => loadMore(num)}
                  startAt={startAt}
                  setStartAt={(num) => setStartAt(num)}
                  totalSplitPlanCount={totalSplitPlanCount}
                  handleDelete={(planId) => handleDelete(planId)}
                  handleEdit={(editedPlan) => handleEdit(editedPlan)}
                />
              </div>
            ) : (
              <div className="mt-3">
                <SplitsTree />
              </div>
            )}
          </>
        ) : (
          showEmptyPlans()
        )
      ) : (
        <PageLoader />
      )}
      <SplitPlanModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={(values) => {
          const newData = data;
          newData.unshift(values);
          setData(newData);
          setStartAt(startAt + 1);
          totalSplitPlanCount === 0 && setCanShow(true);
          setTotalSplitPlanCount(totalSplitPlanCount + 1);
        }}
        plan={props.data}
      />

      <SplitsFilterModal
        visible={filterModal}
        handleCancel={() => setFilterModal(false)}
        handleOk={(values) => {
          getFilteredPlans(values, 0, true);
        }}
        filterBy={filterBy}
      />
    </>
  );
}

export default TopPlanPlans;
